import React, { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNotificationContext } from "./NotificationProvider";
import type { Notification, NotificationDetail } from "../../types/notification";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McCheckbox } from "@maersk-global/mds-react-wrapper/components-core/mc-checkbox";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { handleDownload } from "src/utils/tools";
import PaginationControl from "../Pagination";

dayjs.extend(relativeTime);

interface NotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const PanelHeader = styled.div<any>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px 48px 12px 16px;
  border-bottom: 1px solid #e8eaed;
`;

const HeaderTitle = styled.div<any>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #141414;
`;

const HeaderActions = styled.div<any>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const UnreadBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #ce3e29;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
`;

const PanelBody = styled.div`
  padding: 0 16px 8px;
  max-height: 60vh;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #6f7882;
  font-size: 13px;
  gap: 8px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationRow = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 12px;
  border-bottom: 1px solid #e8eaed;
  cursor: pointer;
  background: ${({ $unread }) => ($unread ? "#f2f9f1" : "transparent")};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $unread }) => ($unread ? "#e7f3e5" : "#f5f7fa")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RowIcon = styled.div<any>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const RowContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RowTitle = styled.div<any>`
  font-size: 14px;
  font-weight: ${({ $unread }) => ($unread ? 600 : 500)};
  color: #141414;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.div`
  font-size: 12px;
  color: #6f7882;
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UnreadDotSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  flex-shrink: 0;
`;

const UnreadDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1668e3;
`;


const FooterPagination = styled.div<any>`
  display: inline-flex;
  align-items: center;

  & > div {
    margin-top: 0;
  }
`;

const DetailBody = styled.div`
  padding: 16px;
`;

const DetailText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #141414;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
`;

const AttachmentBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
`;

const AttachmentLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #4f5f70;
  margin-bottom: 12px;
`;

const DetailFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

type TypeStyle = {
  icon: string;
  bg: string;
  color: string;
  appearance: "success" | "info" | "neutral";
};

const getTypeStyle = (type: string): TypeStyle => {
  switch (type.toUpperCase()) {
    case "EXPORT":
      return { icon: "tray-arrow-up", bg: "#eaf6e7", color: "#1f8a30", appearance: "success" };
    case "IMPORT":
      return { icon: "tray-arrow-down", bg: "#e5f2ff", color: "#1668e3", appearance: "info" };
    default:
      return { icon: "bell", bg: "#f0eaff", color: "#6e3ad4", appearance: "neutral" };
  }
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onClose }) => {
  const {
    notifications,
    loading,
    metadata,
    unreadCount,
    searchNotifications,
    markAsRead,
    getNotificationDetail,
  } = useNotificationContext();

  const [selectedNotification, setSelectedNotification] = useState<NotificationDetail | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (visible) {
      searchNotifications({
        page: currentPage - 1,
        size: pageSize,
        sort: "id desc",
        ...(showUnreadOnly && { isRead: false }),
      });
    }
  }, [visible, currentPage, pageSize, showUnreadOnly, searchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    const detail = await getNotificationDetail(notification.id);
    if (detail) {
      setSelectedNotification(detail);
      setDetailVisible(true);

      const aId = detail.parameters?.ATTACHMENT_ID as string;
      setAttachmentId(aId);

      if (!notification.isRead) {
        markAsRead([notification.id]);
      }
    }
  };

  const handleMarkAllRead = () => {
    const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);
    if (unreadIds.length > 0) {
      markAsRead(unreadIds);
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize === pageSize) return;
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const closeDetail = () => {
    setDetailVisible(false);
    setSelectedNotification(null);
    setAttachmentId(null);
  };

  return (
    <>
      <McModal
        heading=" "
        open={visible}
        closed={onClose}
        dimension="large"
      >
        <PanelHeader>
          <HeaderTitle>
            <McIcon icon="bell" />
            <span>Notifications</span>
            {unreadCount > 0 && <UnreadBadge>{unreadCount}</UnreadBadge>}
          </HeaderTitle>
          <HeaderActions>
            <McButton
              appearance="primary"
              fit="small"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              icon="check"
            >
              Mark All Read
            </McButton>
            <McButton appearance="secondary" fit="small" onClick={onClose}>
              Close
            </McButton>
            <McCheckbox
              label="Show unread only"
              checked={showUnreadOnly}
              change={(e: any) => {
                setShowUnreadOnly(!!e.target.checked);
                setCurrentPage(1);
              }}
            />
          </HeaderActions>
        </PanelHeader>

        <PanelBody>
          {loading ? (
            <LoadingWrapper>
              <McLoadingIndicator />
            </LoadingWrapper>
          ) : !notifications || notifications.length === 0 ? (
            <EmptyState>
              <McIcon icon="bell" style={{ fontSize: 32, opacity: 0.4 }} />
              <span>No notifications</span>
            </EmptyState>
          ) : (
            <>
              <NotificationList>
                {notifications.map((notification) => {
                  const style = getTypeStyle(notification.type);
                  const unread = !notification.isRead;
                  return (
                    <NotificationRow
                      key={notification.id}
                      $unread={unread}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <RowIcon $bg={style.bg} $color={style.color}>
                        <McIcon icon={style.icon} />
                      </RowIcon>
                      <RowContent>
                        <RowTitle $unread={unread}>{notification.title}</RowTitle>
                        <RowMeta>{new Date(notification.createdAt).toLocaleString()}</RowMeta>
                      </RowContent>
                      <RowActions>
                        <McTag appearance={style.appearance} fit="small">
                          {notification.type.toUpperCase()}
                        </McTag>
                        <UnreadDotSlot>{unread && <UnreadDot />}</UnreadDotSlot>
                      </RowActions>
                    </NotificationRow>
                  );
                })}
              </NotificationList>
            </>
          )}
        </PanelBody>

        {(metadata?.totalElements || 0) > 0 && (
          <FooterPagination slot="primaryAction">
            <PaginationControl
              totalItems={metadata?.totalElements || 0}
              currentPage={currentPage}
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100, 300, 500]}
              narrowPagination
              position="right"
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </FooterPagination>
        )}
      </McModal>

      <McModal
        heading={selectedNotification?.title || ""}
        open={detailVisible}
        closed={closeDetail}
        dimension="medium"
      >
        {selectedNotification && (
          <DetailBody>
            <DetailText>{selectedNotification.text}</DetailText>

            {(selectedNotification.parameters?.ATTACHMENT_ID as string) && (
              <AttachmentBox>
                <AttachmentLabel>File Attachment</AttachmentLabel>
                {attachmentId ? (
                  <McButton
                    appearance="primary"
                    loading={downloading}
                    fit="small"
                    icon="tray-arrow-down"
                    click={async () => {
                      setDownloading(true);
                      await handleDownload(attachmentId);
                      setDownloading(false);
                    }}
                  >
                    Download File
                  </McButton>
                ) : (
                  <McLoadingIndicator />
                )}
              </AttachmentBox>
            )}

            <DetailFooter>
              <McButton appearance="neutral" fit="small" onClick={closeDetail}>
                Close
              </McButton>
            </DetailFooter>
          </DetailBody>
        )}
      </McModal>
    </>
  );
};
